import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineDocumentText } from "react-icons/hi";
import {
  formOptions,
  LOADER_STATUS,
  SORT_TYPE,
  USER_ROLE,
} from "../../../Constants/constant";
import CircularLoader from "../../common/CircularLoader/CircularLoader";
import Search from "../../common/Search/Search";
import SelectOption from "../../common/Select/Select";
import TableHeader from "../../common/Table/TableHeader";
import FormsActionButtons from "./FormsActionButtons";
import { formatDateIntoDMYToDisplay, selectedTabState } from "Utils/utils";
import { roleAccess } from "../../../helpers/RolesPermission";
import BulkDeleteFormModal from "./BulkDeleteFormModal";
import { Link } from "react-router-dom";

const EMPTY = "—";

const displayDate = (value) => {
  const formatted = formatDateIntoDMYToDisplay(value);
  return formatted ? formatted : EMPTY;
};

const displayText = (value) => {
  if (value === null || value === undefined || value === "") return EMPTY;
  return value;
};

const SubmittedFormViewTable = ({
  role,
  templateList,
  isLoading,
  queryParams,
  handleDownload,
  handleFetch,
  getSortBy,
  participantsUid,
  participantId,
  selectedPdfId,
  renderLoading,
  parentSlug,
  refreshFormList,
  refreshProgressBar,
}) => {
  const [search, setSearch] = useState(null);
  const [selectedForms, setSelectedForms] = useState([]);
  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState(formOptions[0]);
  const [sortBy, setSortBy] = useState(queryParams.sort_field);
  const [sortDescending, setSortDescending] = useState(
    queryParams.sort_order === SORT_TYPE.DESC
  );

  const showSupervisoryColumn = templateList?.some((item) =>
    item?.title?.includes("Supervisory Meeting Record")
  );

  const columnCount = useMemo(() => {
    let count = 8; // name, status, created by, submitted, updated, expiry, version, actions
    if (showSupervisoryColumn) count += 1;
    return count;
  }, [showSupervisoryColumn]);

  const { completeCount, incompleteCount } = useMemo(() => {
    const list = templateList ?? [];
    return {
      completeCount: list.filter((item) => item?.is_complete).length,
      incompleteCount: list.filter((item) => !item?.is_complete).length,
    };
  }, [templateList]);

  const viewPathPrefix =
    role === "sil"
      ? "sil-accommodation"
      : role === "hrm"
      ? "worker-profile"
      : roleAccess([USER_ROLE.Sp_Standard_User])
      ? "participant-profile"
      : "sp-profile";

  useEffect(() => {
    getSortBy(sortBy);
    return () => {
      if (selectedTabState?.get()) {
        selectedTabState?.save(null);
      }
    };
  }, [sortBy]);

  useEffect(() => {
    setSortBy(queryParams?.sort_field ? queryParams?.sort_field : null);
    if (queryParams?.sort_order) {
      setSortDescending(queryParams.sort_order === SORT_TYPE.DESC);
    }
    if (queryParams.hasOwnProperty("filter") && queryParams?.filter !== null) {
      setSelectedStatus(
        formOptions?.find((x) => x.id === queryParams?.filter?.is_complete[0]) ??
          formOptions[0]
      );
    } else {
      setSelectedStatus(formOptions[0]);
    }
    if (queryParams?.search) {
      setSearch(queryParams.search);
    }
  }, [queryParams]);

  useEffect(() => {
    if (
      ((queryParams?.search === null || queryParams?.search === undefined) &&
        !search) ||
      queryParams?.search === search
    ) {
      return;
    }

    const timeOutId = setTimeout(
      () => handleFetch({ search }, sortBy),
      1000
    );
    return () => clearTimeout(timeOutId);
  }, [search]);

  const flipSort = (by) => {
    setSortBy(by);
    setSortDescending(sortDescending);
    handleFetch(
      {
        sort_field: by,
        sort_order: !sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
      },
      by
    );
  };

  const handleBulkDeleteSuccess = () => {
    setSelectedForms([]);
    refreshFormList();
  };

  return (
    <div className="form-list-card">
      <div className="form-list-toolbar">
        <div className="form-list-toolbar-filters">
          <div className="form-list-filter-field">
            <span className="form-list-filter-label">Status</span>
            <SelectOption
              cssClass="z-[5]"
              isDisabled={isLoading || renderLoading !== LOADER_STATUS.COMPLATE}
              isSearch={false}
              selectedOption={selectedStatus}
              handleChange={(e) => {
                setSelectedStatus(e);
                handleFetch({ is_complete: e.id, page: 1 }, sortBy);
              }}
              data={formOptions}
              useMenuPortal={true}
            />
          </div>
          {!isLoading && templateList?.length > 0 && (
            <div className="form-list-summary">
              <span className="form-status-badge is-complete">
                {completeCount} complete
              </span>
              <span className="form-status-badge is-incomplete">
                {incompleteCount} incomplete
              </span>
            </div>
          )}
        </div>
        <Search
          isDisabled={renderLoading !== LOADER_STATUS.COMPLATE}
          setSearch={setSearch}
          search={search}
        />
      </div>

      <div className="form-list-table-scroll custom-scroll">
        <table className="form-list-table">
          <thead>
            <tr>
              <TableHeader
                sortName="form_name"
                flipSort={flipSort}
                sortBy={sortBy}
                sortDescending={sortDescending}
                label="Form name"
                cssClass="!normal-case !text-[0.6875rem]"
              />
              <TableHeader
                sortName="status"
                flipSort={flipSort}
                sortBy={sortBy}
                sortDescending={sortDescending}
                label="Status"
                cssClass="!normal-case !text-[0.6875rem]"
              />
              {showSupervisoryColumn && (
                <TableHeader
                  isSort={false}
                  label="Supervisory"
                  cssClass="!normal-case !text-[0.6875rem]"
                />
              )}
              <TableHeader
                isSort={false}
                label="Created by"
                cssClass="!normal-case !text-[0.6875rem]"
              />
              <TableHeader
                isSort={false}
                sortName="submitted_date"
                flipSort={flipSort}
                sortBy={sortBy}
                sortDescending={sortDescending}
                label="Submitted"
                cssClass="!normal-case !text-[0.6875rem]"
              />
              <TableHeader
                isSort={false}
                label="Last updated"
                cssClass="!normal-case !text-[0.6875rem]"
              />
              <TableHeader
                isSort={false}
                sortName="expiry_date"
                flipSort={flipSort}
                sortBy={sortBy}
                sortDescending={sortDescending}
                label="Expiry"
                cssClass="!normal-case !text-[0.6875rem]"
              />
              <TableHeader
                isSort={false}
                sortName="version"
                flipSort={flipSort}
                sortBy={sortBy}
                sortDescending={sortDescending}
                label="Ver."
                cssClass="!normal-case !text-[0.6875rem] text-center"
              />
              <th scope="col" className="form-table-actions-col">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columnCount} className="text-center py-10">
                  <CircularLoader
                    classes="flex justify-center items-center"
                    classloader="loader-l"
                  />
                </td>
              </tr>
            ) : templateList?.length > 0 ? (
              templateList.map((item) => {
                const updatedBy = displayText(item?.updated_by);
                const updatedAt = displayDate(item?.updated_at);
                const lastUpdated =
                  updatedBy === EMPTY && updatedAt === EMPTY
                    ? EMPTY
                    : updatedBy !== EMPTY && updatedAt !== EMPTY
                    ? `${updatedBy} · ${updatedAt}`
                    : updatedBy !== EMPTY
                    ? updatedBy
                    : updatedAt;

                return (
                  <tr key={item.uuid}>
                    <td>
                      <Link
                        to={`/${viewPathPrefix}/${parentSlug}/${item.uuid}/view`}
                        state={{ particpantUuid: participantsUid }}
                        className="form-list-form-link capitalize"
                      >
                        {item?.title}
                      </Link>
                    </td>
                    <td>
                      <span
                        className={`form-status-badge ${
                          item?.is_complete ? "is-complete" : "is-incomplete"
                        }`}
                      >
                        {item?.is_complete ? "Complete" : "Incomplete"}
                      </span>
                    </td>
                    {showSupervisoryColumn && (
                      <td className="form-list-cell-muted capitalize">
                        {item?.title?.includes("Supervisory Meeting Record")
                          ? displayDate(item?.created_at)
                          : EMPTY}
                      </td>
                    )}
                    <td className="form-list-cell-muted capitalize">
                      {displayText(item?.created_by)}
                    </td>
                    <td className="form-list-cell-muted">
                      {displayDate(item?.created_at)}
                    </td>
                    <td
                      className={`form-list-cell-muted capitalize ${
                        lastUpdated === EMPTY ? "form-list-cell-empty" : ""
                      }`}
                    >
                      {lastUpdated}
                    </td>
                    <td
                      className={`form-list-cell-muted ${
                        !item?.expiry_date ? "form-list-cell-empty" : ""
                      }`}
                    >
                      {displayDate(item?.expiry_date)}
                    </td>
                    <td className="text-center">
                      <span className="form-list-version">{item?.version ?? 0}</span>
                    </td>
                    <td className="form-table-actions-col">
                      <FormsActionButtons
                        role={role}
                        item={item}
                        parentSlug={parentSlug}
                        participantsUid={participantsUid}
                        participantId={participantId}
                        selectedPdfId={selectedPdfId}
                        handleDownload={handleDownload}
                        refreshFormList={refreshFormList}
                        refreshProgressBar={refreshProgressBar}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              renderLoading === LOADER_STATUS.COMPLATE && (
                <tr>
                  <td colSpan={columnCount}>
                    <div className="form-list-empty">
                      <div className="form-list-empty-icon">
                        <HiOutlineDocumentText className="w-6 h-6" />
                      </div>
                      <p className="form-list-empty-title">No forms found</p>
                      <p className="form-list-empty-desc">
                        Try changing the status filter or search term.
                      </p>
                    </div>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {openBulkDeleteModal && (
        <BulkDeleteFormModal
          open={openBulkDeleteModal}
          setOpen={setOpenBulkDeleteModal}
          selectedForms={selectedForms}
          role={role}
          refreshFormList={handleBulkDeleteSuccess}
          parentSlug={parentSlug}
        />
      )}
    </div>
  );
};

export default SubmittedFormViewTable;
